import { create } from 'zustand';
import api from '../lib/api';

export interface Game {
  id: string;
  title: string;
  slug: string;
  subtitle?: string | null;
  description: string | null;
  short_description: string | null;
  developer: string | null;
  publisher: string | null;
  release_date: string | null;
  price: number;
  sale_price: number | null;
  cover_image_url: string | null;
  banner_image_url: string | null;
  trailer_url: string | null;
  screenshots: Array<string | GameScreenshot> | null;
  tags: string[] | null;
  genre: string | null;
  age_rating?: string | null;
  supported_languages?: string[] | null;
  eula_url?: string | null;
  privacy_policy_url?: string | null;
  website_url?: string | null;
  min_requirements: Record<string, string> | null;
  rec_requirements: Record<string, string> | null;
  requirements?: Record<string, GamePlatformRequirements> | null;
  file_size_mb: number;
  version: string | null;
  is_featured: boolean;
  is_free?: boolean;
  rating_avg: number;
  rating_count: number;
  download_count: number;
  platforms?: GamePlatformInfo[];
  latest_store_version?: GameLatestVersion | null;
  pricing?: GamePricing | null;
  studio?: GameStudio | null;
  more_from_studio?: GameStudioGame[];
  highlights?: GameHighlight[];
  faqs?: GameFaq[];
  review_summary?: GameReviewSummary | null;
  is_owned?: boolean;
  can_review?: boolean;
  is_suspended?: number;
  suspension_reason?: string | null;
  // library fields
  installed?: boolean;
  last_played_at?: string | null;
  play_time_minutes?: number;
  purchased_at?: string;
  reviews?: GameReview[];
}

export interface GameScreenshot {
  id?: number;
  platform?: string;
  url: string;
  sort_order?: number;
}

export interface GameHighlight {
  id?: number;
  title: string;
  description?: string | null;
  icon?: string | null;
}

export interface GameFaq {
  id?: number;
  question: string;
  answer: string;
}

export interface GamePlatformInfo {
  platform: string;
  version?: string | null;
  changelog?: string | null;
  released_at?: string | null;
  build_number?: number | null;
  file_size?: number | null;
}

export interface GamePlatformRequirements {
  minimum?: Record<string, string | null>;
  recommended?: Record<string, string | null>;
}

export interface GameLatestVersion {
  version?: string | null;
  changelog?: string | null;
  released_at?: string | null;
}

export interface GamePricing {
  is_free: boolean;
  price: number;
  sale_price: number | null;
  sale_start?: string | null;
  sale_end?: string | null;
  status?: string | null;
}

export interface GameStudio {
  id?: number;
  name?: string | null;
  logo_url?: string | null;
  website?: string | null;
  bio?: string | null;
  type?: string | null;
  is_verified?: boolean;
  trader?: {
    name: string;
    email: string;
    phone: string;
    address: string;
  };
}

export interface GameStudioGame {
  id: string;
  title: string;
  slug: string;
  cover_image_url: string | null;
  short_description: string | null;
}

export interface GameReviewSummary {
  average: number;
  count: number;
  distribution: Record<string, number>;
}

export interface GameReview {
  id: string;
  user_id: string;
  game_id: string;
  rating: number;
  title: string | null;
  content: string | null;
  helpful_count: number;
  created_at: string;
  username: string;
  avatar_url: string | null;
}

export interface McServer {
  id: string;
  name: string;
  description: string | null;
  address: string;
  port: number;
  version: string | null;
  max_players: number;
  icon_url: string | null;
  banner_url: string | null;
  tags: string[] | null;
  website_url: string | null;
  discord_url: string | null;
  is_featured: boolean;
  player_count: number;
  uptime_percent: number;
}

// ===== PROFILE TYPES =====

export interface ProfileMod {
  name: string;
  file_name: string;
  source: 'modrinth' | 'curseforge' | 'url' | 'custom';
  icon_url?: string;
  source_id?: string;
  version_id?: string;
  download_url?: string;
  sha1?: string;
  size?: number;
  required: boolean;
}

export interface ProfileResourcePack {
  name: string;
  file_name: string;
  source: 'modrinth' | 'curseforge' | 'url' | 'custom';
  source_id?: string;
  version_id?: string;
  download_url?: string;
  sha1?: string;
  size?: number;
}

export interface ProfileShaderPack {
  name: string;
  file_name: string;
  source: 'modrinth' | 'curseforge' | 'url' | 'custom';
  source_id?: string;
  version_id?: string;
  download_url?: string;
  sha1?: string;
  size?: number;
}

export interface McProfile {
  id: string;
  name: string;
  icon: string | null;
  server_description?: string | null;
  banner_url?: string | null;
  video_background_url?: string | null;
  created_at: string;
  updated_at: string;
  source: 'custom' | 'server';
  erozion_server_id: string | null;
  server: {
    address: string;
    port: number;
    auto_connect: boolean;
  };
  minecraft: {
    version: string;
    loader: 'vanilla' | 'forge' | 'fabric' | 'quilt';
    loader_version: string | null;
  };
  mods: ProfileMod[];
  resourcePacks: ProfileResourcePack[];
  shaderPacks: ProfileShaderPack[];
  jvm: {
    min_memory: number | null;
    max_memory: number | null;
    extra_args: string[];
    java_path: string | null;
  };
  allow_user_mods: boolean;
  manifest_version: number;
  last_synced_at: string | null;
  last_played_at: string | null;
}

export interface ReviewLibraryItem {
  id: number;
  game_id: string;
  game_slug?: string;
  store_version_id: number;
  build_id: number;
  added_at: string;
  store_version: string;
  platform: string;
  review_status: string;
  title: string;
  cover_image_url: string | null;
  short_description: string | null;
  file_url: string | null;
  file_name: string | null;
  file_size: number | null;
  build_version: string | null;
  build_number: number | null;
}

export interface GameDownloadItem {
  id: string;
  gameSlug: string;
  fileName: string;
  status: 'downloading' | 'verifying' | 'completed' | 'error';
  progress: number;
  bytesDownloaded?: number;
  totalBytes?: number;
  bytesPerSecond?: number;
  savePath?: string;
  error?: string;
}

// Helper: detect user's platform for Erozion game filtering
function getErozionPlatform(): string | null {
  const api = (window as any).api;
  if (!api?.platform) return null;
  if (api.platform === 'win32') return 'windows';
  if (api.platform === 'linux') return 'linux';
  if (api.platform === 'darwin') {
    // arm64 = Apple Silicon, x64 = Intel
    return api.arch === 'arm64' ? 'macos_silicon' : 'macos_intel';
  }
  return null;
}

type GamingView = 'hub' | 'minecraft' | 'game-detail' | 'store' | 'library' | 'review-library' | 'hosting';
type ProfileSubTab = 'overview' | 'mods' | 'resource-packs' | 'shaders' | 'worlds' | 'settings';

interface GamingState {
  // Navigation
  isGamingHubActive: boolean;
  gamingView: GamingView;
  previousGamingView: GamingView | null;
  selectedGameSlug: string | null;
  reviewingStoreVersionId: number | null;

  // Profile navigation
  selectedProfileId: string | null;
  profileSubTab: ProfileSubTab;
  // Microsoft login modal
  showMicrosoftLogin: boolean;

  // Data
  storeGames: Game[];
  libraryGames: Game[];
  featuredGames: Game[];
  selectedGame: Game | null;
  mcPublicServers: McServer[];
  reviewLibrary: ReviewLibraryItem[];

  // Profiles
  profiles: McProfile[];
  selectedProfile: McProfile | null;
  
  // Loading
  isLoadingStore: boolean;
  isLoadingLibrary: boolean;
  isLoadingGame: boolean;
  isLoadingMcServers: boolean;
  isLoadingProfiles: boolean;
  isLoadingReviewLibrary: boolean;
  isLaunchingProfile: string | null; // profileId being launched
  isSyncingServerProfile: string | null;
  launchProgress: { status: string; message: string; progress?: number; total?: number } | null;
  downloadingGameId: string | null;
  gameDownloads: GameDownloadItem[];
  downloadedGameSlugs: Record<string, string>;
  installedGameVersions: Record<string, string | null>;
  gameUpdatesAvailable: Record<string, string>; // slug → latest version

  // Actions
  setGamingHubActive: (active: boolean) => void;
  setGamingView: (view: GamingView) => void;
  openGameDetail: (slug: string, opts?: { fromReview?: number; gameId?: string }) => void;
  goBackFromDetail: () => void;
  goBackToHub: () => void;

  // Profile actions
  openProfile: (profileId: string) => void;
  closeProfile: () => void;
  setProfileSubTab: (tab: ProfileSubTab) => void;
  fetchProfiles: () => Promise<void>;
  createProfile: (data: Partial<McProfile>) => Promise<McProfile | null>;
  updateProfile: (profileId: string, updates: Partial<McProfile>) => Promise<void>;
  deleteProfile: (profileId: string) => Promise<void>;
  duplicateProfile: (profileId: string) => Promise<void>;
  syncServerProfile: (profileId: string, overrides?: Partial<Pick<McProfile, 'mods' | 'resourcePacks' | 'shaderPacks'>>) => Promise<boolean>;
  launchProfile: (profileId: string) => Promise<void>;
  
  fetchStoreGames: (params?: Record<string, string>) => Promise<void>;
  fetchLibrary: () => Promise<void>;
  fetchGameDetail: (slug: string) => Promise<void>;
  fetchGameDetailById: (gameId: string) => Promise<void>;
  purchaseGame: (gameId: string) => Promise<boolean>;
  submitReview: (gameId: string, rating: number, title?: string, content?: string) => Promise<boolean>;
  fetchMcPublicServers: (params?: Record<string, string>) => Promise<void>;
  fetchReviewLibrary: () => Promise<void>;
  downloadGame: (gameId: string, gameSlug?: string) => Promise<void>;
  downloadReviewBuild: (storeVersionId: number) => Promise<void>;
  openDownloadedGame: (gameSlug: string, opts?: { gameId?: string; reviewStoreVersionId?: number }) => Promise<boolean>;
  createGameShortcut: (gameSlug: string, opts?: { title?: string; reviewStoreVersionId?: number }) => Promise<boolean>;
  uninstallGame: (gameSlug: string, title?: string) => Promise<boolean>;
  initGameDownloadEvents: () => void;
  dismissGameDownload: (downloadId: string) => void;
  checkGameUpdates: () => Promise<void>;
  updateGame: (gameId: string, gameSlug: string) => Promise<void>;
}

const isElectron = !!(window as any).api?.isElectron;
const electronApi = (window as any).api;
let gameDownloadCleanup: (() => void) | null = null;

export const useGamingStore = create<GamingState>((set, get) => ({
  isGamingHubActive: false,
  gamingView: 'hub',
  previousGamingView: null,
  selectedGameSlug: null,
  reviewingStoreVersionId: null,

  selectedProfileId: null,
  profileSubTab: 'overview',
  showMicrosoftLogin: false,

  storeGames: [],
  libraryGames: [],
  featuredGames: [],
  selectedGame: null,
  mcPublicServers: [],
  reviewLibrary: [],

  profiles: [],
  selectedProfile: null,

  isLoadingStore: false,
  isLoadingLibrary: false,
  isLoadingGame: false,
  isLoadingMcServers: false,
  isLoadingProfiles: false,
  isLoadingReviewLibrary: false,
  isLaunchingProfile: null,
  isSyncingServerProfile: null,
  launchProgress: null,
  downloadingGameId: null,
  gameDownloads: [],
  downloadedGameSlugs: {},
  installedGameVersions: {},
  gameUpdatesAvailable: {},

  setGamingHubActive: (active) => {
    set({ isGamingHubActive: active });
    if (active) {
      set({ gamingView: 'hub' });
      get().fetchStoreGames({ featured: '1' });
      get().fetchLibrary();
      if (localStorage.getItem('is_admin') === '1') {
        get().fetchReviewLibrary();
      }
    }
  },

  setGamingView: (view) => set({ gamingView: view }),

  openGameDetail: (slug, opts) => {
    set({
      previousGamingView: get().gamingView,
      gamingView: 'game-detail',
      selectedGameSlug: slug,
      reviewingStoreVersionId: opts?.fromReview ?? null,
    });
    if (opts?.fromReview && opts?.gameId) {
      get().fetchGameDetailById(opts.gameId);
    } else {
      get().fetchGameDetail(slug);
    }
  },

  goBackFromDetail: () => {
    const prev = get().previousGamingView;
    set({
      gamingView: prev || 'hub',
      selectedGame: null,
      selectedGameSlug: null,
      previousGamingView: null,
      reviewingStoreVersionId: null,
    });
  },

  goBackToHub: () => set({ gamingView: 'hub', selectedGame: null, selectedGameSlug: null, selectedProfileId: null, selectedProfile: null, previousGamingView: null, reviewingStoreVersionId: null }),

  // Profile actions
  openProfile: (profileId) => {
    const profile = get().profiles.find(p => p.id === profileId) || null;
    set({ selectedProfileId: profileId, selectedProfile: profile, profileSubTab: 'overview' });
  },

  closeProfile: () => {
    set({ selectedProfileId: null, selectedProfile: null, profileSubTab: 'overview' });
  },

  // Microsoft login modal controls
  openMicrosoftLogin: () => set({ showMicrosoftLogin: true }),
  closeMicrosoftLogin: () => set({ showMicrosoftLogin: false }),

  setProfileSubTab: (tab) => set({ profileSubTab: tab }),

  fetchProfiles: async () => {
    if (!isElectron) return;
    set({ isLoadingProfiles: true });
    try {
      const result = await electronApi.profileList();
      if (result.success) {
        let profiles = result.data as McProfile[];

        // Sync owned server manifests from DB -> local server profiles.
        // Server profiles are managed by Erozion server settings and replicated locally.
        try {
          const userId = localStorage.getItem('user_id');
          const servers = await api.get('/servers').then(r => r.data).catch(() => []);
          const ownedServers = Array.isArray(servers)
            ? servers.filter((s: any) => s?.owner_id && userId && s.owner_id === userId)
            : [];

          let syncedSomething = false;

          for (const server of ownedServers) {
            const serverId = server.id;
            if (!serverId) continue;

            const mcInfo = await api.get(`/servers/${serverId}/minecraft`).then(r => r.data).catch(() => null);
            if (!mcInfo) continue;

            // Only server profiles with non-vanilla loader are mirrored locally
            if (!mcInfo.mod_loader || mcInfo.mod_loader === 'vanilla') continue;

            const manifest = await api.get(`/servers/${serverId}/minecraft/manifest`).then(r => r.data).catch(() => null);
            if (!manifest) continue;

            const existing = profiles.find((p) => p.source === 'server' && p.erozion_server_id === serverId);

            const serverManifest = {
              manifest_version: manifest.manifest_version || 1,
              allow_user_mods: !!manifest.allow_user_mods,
              server_description: manifest.server_description ?? manifest.description ?? mcInfo.description ?? server.description ?? null,
              banner_url: mcInfo.banner_url || null,
              video_background_url: mcInfo.video_background_url || null,
              server: {
                address: manifest.server?.address || mcInfo.address || '',
                port: manifest.server?.port || mcInfo.port || 25565,
                auto_connect: manifest.server?.auto_connect ?? true,
              },
              minecraft: {
                version: manifest.minecraft?.version || mcInfo.version,
                loader: manifest.minecraft?.loader || mcInfo.mod_loader,
                loader_version: manifest.minecraft?.loader_version ?? mcInfo.mod_loader_version ?? null,
              },
              mods: manifest.mods || [],
              resourcePacks: manifest.resourcePacks || [],
              shaderPacks: manifest.shaderPacks || [],
            };

            if (!existing) {
              await electronApi.profileCreate({
                name: manifest.name || mcInfo.name || server.name || 'Profil serveur',
                source: 'server',
                erozion_server_id: serverId,
                manifest_version: serverManifest.manifest_version,
                allow_user_mods: serverManifest.allow_user_mods,
                server_description: serverManifest.server_description,
                banner_url: serverManifest.banner_url,
                video_background_url: serverManifest.video_background_url,
                server: serverManifest.server,
                minecraft: serverManifest.minecraft,
                mods: serverManifest.mods,
                resourcePacks: serverManifest.resourcePacks,
                shaderPacks: serverManifest.shaderPacks,
              });
              syncedSomething = true;
            } else if (
              (manifest.manifest_version || 1) > (existing.manifest_version || 1)
              || (existing.server_description || null) !== (serverManifest.server_description || null)
              || (existing.banner_url || null) !== (serverManifest.banner_url || null)
              || (existing.video_background_url || null) !== (serverManifest.video_background_url || null)
            ) {
              await electronApi.profileSync(existing.id, serverManifest);
              syncedSomething = true;
            }
          }

          if (syncedSomething) {
            const refreshed = await electronApi.profileList();
            if (refreshed.success) profiles = refreshed.data as McProfile[];
          }
        } catch (syncErr) {
          console.warn('Server profile sync skipped:', syncErr);
        }

        set({ profiles });
        // Update selectedProfile if it's open
        const { selectedProfileId } = get();
        if (selectedProfileId) {
          const updated = profiles.find((p: McProfile) => p.id === selectedProfileId);
          if (updated) set({ selectedProfile: updated });
        }
      }
    } catch (e) {
      console.error('Failed to fetch profiles:', e);
    } finally {
      set({ isLoadingProfiles: false });
    }
  },

  createProfile: async (data) => {
    if (!isElectron) return null;
    try {
      const result = await electronApi.profileCreate(data);
      if (result.success) {
        await get().fetchProfiles();
        return result.data;
      }
      return null;
    } catch (e) {
      console.error('Failed to create profile:', e);
      return null;
    }
  },

  updateProfile: async (profileId, updates) => {
    if (!isElectron) return;
    try {
      const current = get().profiles.find((p) => p.id === profileId) || null;

      // For server profiles, push changes to DB first (owner/admin flow),
      // then bump manifest version so other clients re-sync.
      if (current?.source === 'server' && current.erozion_server_id) {
        const serverId = current.erozion_server_id;
        const merged: McProfile = {
          ...current,
          ...updates,
          minecraft: { ...current.minecraft, ...(updates.minecraft || {}) },
          server: { ...current.server, ...(updates.server || {}) },
          jvm: { ...current.jvm, ...(updates.jvm || {}) },
          mods: (updates.mods ?? current.mods) as any,
          resourcePacks: (updates.resourcePacks ?? current.resourcePacks) as any,
          shaderPacks: (updates.shaderPacks ?? current.shaderPacks) as any,
        };

        await api.put(`/servers/${serverId}/minecraft`, {
          name: merged.name,
          version: merged.minecraft.version,
          address: merged.server.address,
          port: merged.server.port,
          mod_loader: merged.minecraft.loader,
          mod_loader_version: merged.minecraft.loader_version,
        });

        // Bumps manifest_version on backend
        await api.put(`/servers/${serverId}/minecraft/manifest`, {
          allow_user_mods: merged.allow_user_mods,
          auto_connect: merged.server.auto_connect,
        });
      }

      const result = await electronApi.profileUpdate(profileId, updates);
      if (result.success) {
        await get().fetchProfiles();
      }
    } catch (e) {
      console.error('Failed to update profile:', e);
    }
  },

  deleteProfile: async (profileId) => {
    if (!isElectron) return;
    try {
      const result = await electronApi.profileDelete(profileId);
      if (result.success) {
        if (get().selectedProfileId === profileId) {
          set({ selectedProfileId: null, selectedProfile: null });
        }
        await get().fetchProfiles();
      }
    } catch (e) {
      console.error('Failed to delete profile:', e);
    }
  },

  duplicateProfile: async (profileId) => {
    if (!isElectron) return;
    try {
      const result = await electronApi.profileDuplicate(profileId);
      if (result.success) {
        await get().fetchProfiles();
      }
    } catch (e) {
      console.error('Failed to duplicate profile:', e);
    }
  },

  syncServerProfile: async (profileId, overrides) => {
    if (!isElectron) return false;
    const current = get().profiles.find((p) => p.id === profileId);
    if (!current || current.source !== 'server' || !current.erozion_server_id) return false;

    const serverId = current.erozion_server_id;
    set({ isSyncingServerProfile: profileId });
    try {
      const userId = localStorage.getItem('user_id');
      const server = await api.get(`/servers/${serverId}`).then(r => r.data);
      if (!server?.owner_id || !userId || server.owner_id !== userId) {
        throw new Error('Seul le propriétaire du serveur peut synchroniser le profil.');
      }

      const latestLocalResult = await electronApi.profileGet(profileId);
      const latestLocal: McProfile = latestLocalResult?.success ? latestLocalResult.data : current;

      const localProfile: McProfile = {
        ...latestLocal,
        mods: (overrides?.mods ?? latestLocal.mods) as any,
        resourcePacks: (overrides?.resourcePacks ?? latestLocal.resourcePacks) as any,
        shaderPacks: (overrides?.shaderPacks ?? latestLocal.shaderPacks) as any,
      };

      // Push base minecraft/server settings
      await api.put(`/servers/${serverId}/minecraft`, {
        name: localProfile.name,
        version: localProfile.minecraft.version,
        address: localProfile.server.address,
        port: localProfile.server.port,
        mod_loader: localProfile.minecraft.loader,
        mod_loader_version: localProfile.minecraft.loader_version,
      });

      const remoteManifest = await api.get(`/servers/${serverId}/minecraft/manifest`).then(r => r.data);

      const normalizeBool = (v: any) => (v === 1 || v === true || v === '1');
      const keyFor = (item: any) => `${item.sha1 || ''}|${item.version_id || ''}|${item.file_name || ''}|${item.source || ''}`;

      const syncCategory = async (localItems: any[], remoteItems: any[], addUrl: string, deleteUrlPrefix: string, includeRequired: boolean) => {
        const localMap = new Map((localItems || []).map((i) => [keyFor(i), i]));
        const remoteMap = new Map((remoteItems || []).map((i) => [keyFor(i), i]));

        for (const [key, remote] of remoteMap.entries()) {
          if (!localMap.has(key) && remote.id) {
            await api.delete(`${deleteUrlPrefix}/${remote.id}`);
          }
        }

        for (const [key, local] of localMap.entries()) {
          if (!remoteMap.has(key)) {
            const payload: any = {
              name: local.name,
              file_name: local.file_name,
              source: local.source,
              source_id: local.source_id || null,
              version_id: local.version_id || null,
              download_url: local.download_url || null,
              sha1: local.sha1 || null,
              size: local.size || 0,
            };
            if (includeRequired) payload.required = !!local.required;
            await api.post(addUrl, payload);
          }
        }
      };

      await syncCategory(
        localProfile.mods || [],
        remoteManifest.mods || [],
        `/servers/${serverId}/minecraft/manifest/mods`,
        `/servers/${serverId}/minecraft/manifest/mods`,
        true,
      );

      await syncCategory(
        localProfile.resourcePacks || [],
        remoteManifest.resourcePacks || [],
        `/servers/${serverId}/minecraft/manifest/resource-packs`,
        `/servers/${serverId}/minecraft/manifest/resource-packs`,
        false,
      );

      await syncCategory(
        localProfile.shaderPacks || [],
        remoteManifest.shaderPacks || [],
        `/servers/${serverId}/minecraft/manifest/shaders`,
        `/servers/${serverId}/minecraft/manifest/shaders`,
        false,
      );

      // Bump manifest version so all other clients re-download.
      await api.put(`/servers/${serverId}/minecraft/manifest`, {
        allow_user_mods: localProfile.allow_user_mods,
        auto_connect: localProfile.server.auto_connect,
      });

      // Pull back updated manifest version and sync local profile copy.
      const refreshedManifest = await api.get(`/servers/${serverId}/minecraft/manifest`).then(r => r.data);
      await electronApi.profileSync(profileId, {
        manifest_version: refreshedManifest.manifest_version || ((localProfile.manifest_version || 1) + 1),
        allow_user_mods: !!refreshedManifest.allow_user_mods,
        server: {
          address: refreshedManifest.server?.address || localProfile.server.address,
          port: refreshedManifest.server?.port || localProfile.server.port,
          auto_connect: refreshedManifest.server?.auto_connect ?? localProfile.server.auto_connect,
        },
        minecraft: {
          version: refreshedManifest.minecraft?.version || localProfile.minecraft.version,
          loader: refreshedManifest.minecraft?.loader || localProfile.minecraft.loader,
          loader_version: refreshedManifest.minecraft?.loader_version ?? localProfile.minecraft.loader_version,
        },
        mods: (refreshedManifest.mods || []).map((m: any) => ({ ...m, required: m.required !== undefined ? normalizeBool(m.required) : true })),
        resourcePacks: refreshedManifest.resourcePacks || [],
        shaderPacks: refreshedManifest.shaderPacks || [],
      });

      await get().fetchProfiles();
      return true;
    } catch (e) {
      console.error('Failed to sync server profile:', e);
      return false;
    } finally {
      set({ isSyncingServerProfile: null });
    }
  },

  launchProfile: async (profileId) => {
    if (!isElectron) return;
    // Ensure user is authenticated before launching; if not, show Microsoft login modal
    try {
      const current = await electronApi.getCurrentUser();
      if (!current || !current.success) {
        set({ showMicrosoftLogin: true });
        return;
      }
    } catch (e) {
      set({ showMicrosoftLogin: true });
      return;
    }
    set({ isLaunchingProfile: profileId, launchProgress: { status: 'starting', message: 'Démarrage...' } });

    // Subscribe to launch progress events
    const cleanup = electronApi.onMinecraftLaunching?.((data: any) => {
      set({ launchProgress: data });
    });

    // Listen for early crash — surface the log content to the user
    let gotError = false;
    const errorCleanup = electronApi.onMinecraftError?.((data: any) => {
      gotError = true;
      const msg = data.log
        ? `${data.error}\n\nLog (fin):\n${data.log}`
        : data.error;
      set({ isLaunchingProfile: profileId, launchProgress: { status: 'error', message: msg } });
      console.error('[launchProfile] Minecraft error:', msg);
      // Keep the error visible for 20 s then clear
      setTimeout(() => {
        cleanup?.();
        errorCleanup?.();
        set((s) => s.isLaunchingProfile === profileId
          ? { isLaunchingProfile: null, launchProgress: null }
          : {});
      }, 20000);
    });

    try {
      const res = await electronApi.launchProfile(profileId);
      if (res && res.success === false && res.error === 'not_authenticated') {
        // Open Microsoft login modal when native launch reports not authenticated
        set({ showMicrosoftLogin: true });
        cleanup?.();
        return;
      }
      // IPC resolves immediately after spawn — game is now running.
      // Keep listeners alive but clear the "launching" UI state.
      if (!gotError) {
        cleanup?.();
        set({ isLaunchingProfile: null, launchProgress: null });
      }
      // errorCleanup stays alive to catch crashes later
    } catch (e) {
      console.error('Failed to launch profile:', e);
      cleanup?.();
      errorCleanup?.();
      set({ isLaunchingProfile: null, launchProgress: null });
    }
  },

  fetchStoreGames: async (params) => {
    set({ isLoadingStore: true });
    try {
      const effectiveParams = { ...params };
      const platform = getErozionPlatform();
      if (platform) effectiveParams.platform = platform;
      const searchParams = new URLSearchParams(effectiveParams);
      const { data } = await api.get(`/games?${searchParams.toString()}`);
      const games = Array.isArray(data) ? data : [];
      if (params?.featured === '1') {
        set({ featuredGames: games });
      }
      set({ storeGames: games });
    } catch (e) {
      console.error('Failed to fetch store games:', e);
      // Use dummy data as fallback
      set({ storeGames: FALLBACK_GAMES, featuredGames: FALLBACK_GAMES.filter(g => g.is_featured) });
    } finally {
      set({ isLoadingStore: false });
    }
  },

  fetchLibrary: async () => {
    set({ isLoadingLibrary: true });
    try {
      const { data } = await api.get('/games/library/me');
      set({ libraryGames: Array.isArray(data) ? data : [] });
      // Auto-check for game updates after library loads
      get().checkGameUpdates();
    } catch (e) {
      console.error('Failed to fetch library:', e);
      set({ libraryGames: [] });
    } finally {
      set({ isLoadingLibrary: false });
    }
  },

  fetchGameDetail: async (slug) => {
    set({ isLoadingGame: true });
    try {
      const { data } = await api.get(`/games/${slug}/store-page`);
      set({ selectedGame: data });
    } catch (e) {
      console.error('Failed to fetch game detail:', e);
      // Fallback to local game data
      const local = get().storeGames.find(g => g.slug === slug) || FALLBACK_GAMES.find(g => g.slug === slug) || null;
      set({ selectedGame: local });
    } finally {
      set({ isLoadingGame: false });
    }
  },

  fetchGameDetailById: async (gameId) => {
    set({ isLoadingGame: true });
    try {
      const { data } = await api.get(`/admin/games/${gameId}`);
      set({ selectedGame: data });
    } catch (e) {
      console.error('Failed to fetch admin game detail:', e);
      set({ selectedGame: null });
    } finally {
      set({ isLoadingGame: false });
    }
  },

  purchaseGame: async (gameId) => {
    try {
      const { data } = await api.post(`/games/${gameId}/purchase`, { useWallet: false });
      if (data.completed) {
        get().fetchLibrary();
      }
      return !!data.completed;
    } catch (e) {
      console.error('Failed to purchase game:', e);
      return false;
    }
  },

  submitReview: async (gameId, rating, title, content) => {
    try {
      await api.post(`/games/${gameId}/reviews`, { rating, title, content });
      if (get().selectedGameSlug) get().fetchGameDetail(get().selectedGameSlug!);
      return true;
    } catch (e) {
      console.error('Failed to submit review:', e);
      return false;
    }
  },

  fetchMcPublicServers: async (params) => {
    set({ isLoadingMcServers: true });
    try {
      const searchParams = new URLSearchParams(params);
      const { data } = await api.get(`/minecraft/servers/public?${searchParams.toString()}`);
      set({ mcPublicServers: Array.isArray(data) ? data : [] });
    } catch (e) {
      console.error('Failed to fetch MC servers:', e);
      set({ mcPublicServers: FALLBACK_MC_SERVERS });
    } finally {
      set({ isLoadingMcServers: false });
    }
  },

  fetchReviewLibrary: async () => {
    set({ isLoadingReviewLibrary: true });
    try {
      const { data } = await api.get('/admin/review-library');
      set({ reviewLibrary: Array.isArray(data) ? data : [] });
    } catch (e) {
      console.error('Failed to fetch review library:', e);
      set({ reviewLibrary: [] });
    } finally {
      set({ isLoadingReviewLibrary: false });
    }
  },

  initGameDownloadEvents: () => {
    if (!isElectron || !electronApi?.onGameDownloadUpdate || gameDownloadCleanup) return;

    // Populate already-installed games from disk so the "Lancer" button shows
    // immediately even after an app restart (no need to re-download).
    if (electronApi?.getInstalledGames) {
      electronApi.getInstalledGames().then((installedMap: Record<string, string | { path: string; version: string | null }>) => {
        if (installedMap && typeof installedMap === 'object') {
          const slugPaths: Record<string, string> = {};
          const slugVersions: Record<string, string | null> = {};
          for (const [slug, val] of Object.entries(installedMap)) {
            if (typeof val === 'string') {
              // Legacy format: slug → path
              slugPaths[slug] = val;
              slugVersions[slug] = null;
            } else if (val && typeof val === 'object') {
              slugPaths[slug] = val.path;
              slugVersions[slug] = val.version || null;
            }
          }
          set((state) => ({
            downloadedGameSlugs: { ...slugPaths, ...state.downloadedGameSlugs },
            installedGameVersions: { ...slugVersions, ...state.installedGameVersions },
          }));
          // Check for updates once installed versions are known
          get().checkGameUpdates();
        }
      }).catch(() => {});
    }

    gameDownloadCleanup = electronApi.onGameDownloadUpdate((update: GameDownloadItem) => {
      set((state) => {
        const existingIndex = state.gameDownloads.findIndex((d) => d.id === update.id);
        const downloadedGameSlugs = { ...state.downloadedGameSlugs };
        if (update.status === 'completed') {
          downloadedGameSlugs[update.gameSlug] = update.savePath || '';
          // Refresh installed versions from disk after download completes
          if (electronApi?.getInstalledGames) {
            electronApi.getInstalledGames().then((installedMap: Record<string, { path: string; version: string | null }>) => {
              if (installedMap && typeof installedMap === 'object') {
                const slugVersions: Record<string, string | null> = {};
                for (const [slug, val] of Object.entries(installedMap)) {
                  if (val && typeof val === 'object') slugVersions[slug] = val.version || null;
                }
                set({ installedGameVersions: slugVersions });
              }
            }).catch(() => {});
          }
        }

        if (existingIndex === -1) {
          return { gameDownloads: [update, ...state.gameDownloads], downloadedGameSlugs };
        }

        const next = [...state.gameDownloads];
        next[existingIndex] = { ...next[existingIndex], ...update };
        return { gameDownloads: next, downloadedGameSlugs };
      });
    });
  },

  openDownloadedGame: async (gameSlug, opts) => {
    try {
      if (!isElectron || !electronApi?.openGameFolder) return false;

      const reviewStoreVersionId = opts?.reviewStoreVersionId ?? null;
      const explicitGameId = opts?.gameId ?? null;
      const resolvedGameId =
        explicitGameId ||
        get().libraryGames.find((g) => g.slug === gameSlug)?.id ||
        get().storeGames.find((g) => g.slug === gameSlug)?.id ||
        get().selectedGame?.id ||
        null;

      let launchToken: string | null = null;
      const clientToken = electronApi?.getClientToken
        ? await electronApi.getClientToken().catch(() => '')
        : '';
      const requestConfig = clientToken
        ? { headers: { 'x-erozion-client-token': clientToken } }
        : undefined;

      try {
        if (reviewStoreVersionId != null) {
          const { data } = await api.get(`/admin/review-library/${reviewStoreVersionId}/launch-token`, requestConfig);
          launchToken = data?.launch_token || null;
        } else if (resolvedGameId) {
          const { data } = await api.get(`/games/${resolvedGameId}/launch-token`, requestConfig);
          launchToken = data?.launch_token || null;
        } else {
          const { data } = await api.get(`/games/slug/${encodeURIComponent(gameSlug)}/launch-token`, requestConfig);
          launchToken = data?.launch_token || null;
        }
      } catch (e: any) {
        // Offline fallback: Electron can still launch with a valid cached token.
        console.warn('[launch] Failed to fetch launch token:', e?.response?.status, e?.response?.data || e?.message);
        launchToken = null;
      }

      const result = await electronApi.openGameFolder({
        gameSlug,
        launchToken: launchToken || undefined,
        reviewStoreVersionId: reviewStoreVersionId ?? undefined,
      });
      if (!result?.success) {
        console.error('[launch] openGameFolder failed:', result?.error);
      }
      return !!result?.success;
    } catch (e) {
      console.error('Failed to open downloaded game folder:', e);
      return false;
    }
  },

  createGameShortcut: async (gameSlug, opts) => {
    try {
      if (!isElectron || !electronApi?.createGameShortcut) return false;
      const result = await electronApi.createGameShortcut({
        gameSlug,
        title: opts?.title,
        reviewStoreVersionId: opts?.reviewStoreVersionId,
      });
      return !!result?.success;
    } catch (e) {
      console.error('Failed to create game shortcut:', e);
      return false;
    }
  },

  uninstallGame: async (gameSlug, title) => {
    try {
      if (!isElectron || !electronApi?.uninstallGame) return false;
      const result = await electronApi.uninstallGame({ gameSlug, title });
      if (result?.success) {
        set((state) => {
          const updated = { ...state.downloadedGameSlugs };
          delete updated[gameSlug];
          const updatedVersions = { ...state.installedGameVersions };
          delete updatedVersions[gameSlug];
          const updatedUpdates = { ...state.gameUpdatesAvailable };
          delete updatedUpdates[gameSlug];
          return { downloadedGameSlugs: updated, installedGameVersions: updatedVersions, gameUpdatesAvailable: updatedUpdates };
        });
      }
      return !!result?.success;
    } catch (e) {
      console.error('Failed to uninstall game:', e);
      return false;
    }
  },

  dismissGameDownload: (downloadId) => {
    set((state) => ({
      gameDownloads: state.gameDownloads.filter((download) => download.id !== downloadId),
    }));
  },

  downloadGame: async (gameId, gameSlug) => {
    set({ downloadingGameId: gameId });
    try {
      const platform = getErozionPlatform();
      if (!platform) throw new Error('Platform not detected');
      const { data } = await api.get(`/games/${gameId}/download?platform=${platform}`);
      if (data.download_url) {
        const resolvedSlug =
          gameSlug ||
          data.game_slug ||
          get().libraryGames.find((g) => g.id === gameId)?.slug ||
          get().storeGames.find((g) => g.id === gameId)?.slug ||
          `game-${gameId}`;

        const gameTitle =
          get().libraryGames.find((g) => g.id === gameId)?.title ||
          get().storeGames.find((g) => g.id === gameId)?.title ||
          get().selectedGame?.title ||
          null;

        if (isElectron && electronApi?.startGameDownload) {
          await electronApi.startGameDownload({
            downloadUrl: data.download_url,
            gameSlug: resolvedSlug,
            fileName: data.file_name,
            launchExecutablePath: data.launch_executable_path || null,
            installExecutablePath: data.install_executable_path || null,
            gameTitle,
            gameVersion: data.version || null,
          });
        } else {
          window.open(data.download_url, '_blank');
        }
      }
    } catch (e) {
      console.error('Failed to download game:', e);
    } finally {
      set({ downloadingGameId: null });
    }
  },

  downloadReviewBuild: async (storeVersionId) => {
    set({ downloadingGameId: String(storeVersionId) });
    try {
      const { data } = await api.get(`/admin/review-library/${storeVersionId}/download`);
      if (data.download_url) {
        const reviewItem = get().reviewLibrary.find((item) => item.store_version_id === storeVersionId);
        const resolvedSlug = data.game_slug || reviewItem?.game_slug || `review-${storeVersionId}`;

        if (isElectron && electronApi?.startGameDownload) {
          await electronApi.startGameDownload({
            downloadUrl: data.download_url,
            gameSlug: resolvedSlug,
            fileName: data.file_name,
            launchExecutablePath: data.launch_executable_path || null,
            installExecutablePath: data.install_executable_path || null,
          });
        } else {
          window.open(data.download_url, '_blank');
        }
      }
    } catch (e) {
      console.error('Failed to download review build:', e);
    } finally {
      set({ downloadingGameId: null });
    }
  },

  checkGameUpdates: async () => {
    try {
      const { installedGameVersions } = get();
      const entries = Object.entries(installedGameVersions).filter(([, v]) => v);
      if (entries.length === 0) return;

      const games = entries.map(([slug, version]) => ({ slug, version }));
      const { data } = await api.post('/games/check-updates', { games });
      if (data?.updates && typeof data.updates === 'object') {
        const mapped: Record<string, string> = {};
        for (const [slug, info] of Object.entries(data.updates)) {
          mapped[slug] = (info as any).latest_version || String(info);
        }
        set({ gameUpdatesAvailable: mapped });
      }
    } catch (e) {
      console.error('Failed to check game updates:', e);
    }
  },

  updateGame: async (gameId, gameSlug) => {
    // An update is essentially a re-download of the latest version
    await get().downloadGame(gameId, gameSlug);
    // After download starts, clear the update flag for this slug
    set((state) => {
      const updated = { ...state.gameUpdatesAvailable };
      delete updated[gameSlug];
      return { gameUpdatesAvailable: updated };
    });
  },
}));

// Fallback dummy data for when API is unavailable
const FALLBACK_GAMES: Game[] = [
  {
    id: 'dummy-1', title: 'Astral Drift', slug: 'astral-drift',
    description: 'Explorez un univers spatial infini dans ce jeu de survie en monde ouvert. Construisez votre vaisseau, explorez des planètes et survivez aux menaces extraterrestres.',
    short_description: 'Jeu de survie spatiale en monde ouvert',
    developer: 'Erozion Studios', publisher: 'Erozion', release_date: null,
    price: 24.99, sale_price: 19.99,
    cover_image_url: 'https://images.unsplash.com/photo-1614732414444-096e5f1122d5?w=400&h=600&fit=crop',
    banner_image_url: 'https://images.unsplash.com/photo-1614732414444-096e5f1122d5?w=1200&h=400&fit=crop',
    trailer_url: null, screenshots: null,
    tags: ['survival', 'space', 'open-world', 'crafting'], genre: 'Survival',
    min_requirements: null, rec_requirements: null,
    file_size_mb: 4500, version: '0.8.2', is_featured: true,
    rating_avg: 4.3, rating_count: 128, download_count: 2340,
  },
  {
    id: 'dummy-2', title: 'Neon Pulse', slug: 'neon-pulse',
    description: 'Un FPS cyberpunk rapide et intense. Parcourez les rues néon de Neo-Tokyo et éliminez les gangs corrompus dans des combats effrénés.',
    short_description: 'FPS cyberpunk rapide et intense',
    developer: 'Pulse Interactive', publisher: 'Erozion', release_date: null,
    price: 29.99, sale_price: null,
    cover_image_url: 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=400&h=600&fit=crop',
    banner_image_url: 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=1200&h=400&fit=crop',
    trailer_url: null, screenshots: null,
    tags: ['fps', 'cyberpunk', 'action', 'multiplayer'], genre: 'FPS',
    min_requirements: null, rec_requirements: null,
    file_size_mb: 8200, version: '1.2.0', is_featured: true,
    rating_avg: 4.7, rating_count: 512, download_count: 8900,
  },
  {
    id: 'dummy-3', title: 'Verdant', slug: 'verdant',
    description: 'Jeu de construction de civilisation et farming dans un monde fantastique.',
    short_description: 'Farming & civilization builder fantastique',
    developer: 'Greenleaf Games', publisher: 'Erozion', release_date: null,
    price: 0, sale_price: null,
    cover_image_url: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=400&h=600&fit=crop',
    banner_image_url: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=1200&h=400&fit=crop',
    trailer_url: null, screenshots: null,
    tags: ['farming', 'fantasy', 'building', 'coop'], genre: 'Simulation',
    min_requirements: null, rec_requirements: null,
    file_size_mb: 2100, version: '2.1.0', is_featured: false,
    rating_avg: 4.5, rating_count: 320, download_count: 5600,
  },
  {
    id: 'dummy-4', title: 'Shadow Protocol', slug: 'shadow-protocol',
    description: 'Un jeu de stealth tactique où vous incarnez un agent secret.',
    short_description: 'Stealth tactique et espionnage',
    developer: 'DarkByte Studios', publisher: 'Erozion', release_date: null,
    price: 19.99, sale_price: 14.99,
    cover_image_url: 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=400&h=600&fit=crop',
    banner_image_url: 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=1200&h=400&fit=crop',
    trailer_url: null, screenshots: null,
    tags: ['stealth', 'tactical', 'singleplayer', 'action'], genre: 'Action',
    min_requirements: null, rec_requirements: null,
    file_size_mb: 6800, version: '1.0.3', is_featured: false,
    rating_avg: 4.1, rating_count: 87, download_count: 1200,
  },
  {
    id: 'dummy-5', title: 'Rift Runners', slug: 'rift-runners',
    description: 'Jeu de course multijoueur à travers des portails dimensionnels.',
    short_description: 'Course multijoueur inter-dimensionnelle',
    developer: 'Velocity Works', publisher: 'Erozion', release_date: null,
    price: 14.99, sale_price: null,
    cover_image_url: 'https://images.unsplash.com/photo-1511882150382-421056c89033?w=400&h=600&fit=crop',
    banner_image_url: 'https://images.unsplash.com/photo-1511882150382-421056c89033?w=1200&h=400&fit=crop',
    trailer_url: null, screenshots: null,
    tags: ['racing', 'multiplayer', 'arcade', 'sci-fi'], genre: 'Racing',
    min_requirements: null, rec_requirements: null,
    file_size_mb: 3400, version: '1.5.0', is_featured: true,
    rating_avg: 4.0, rating_count: 203, download_count: 3400,
  },
  {
    id: 'dummy-6', title: 'Echoes of Eternity', slug: 'echoes-of-eternity',
    description: 'RPG narratif dans un monde post-apocalyptique. Vos choix façonnent l\'histoire.',
    short_description: 'RPG narratif post-apocalyptique',
    developer: 'Erozion Studios', publisher: 'Erozion', release_date: null,
    price: 34.99, sale_price: null,
    cover_image_url: 'https://images.unsplash.com/photo-1509198397868-475647b2a1e5?w=400&h=600&fit=crop',
    banner_image_url: 'https://images.unsplash.com/photo-1509198397868-475647b2a1e5?w=1200&h=400&fit=crop',
    trailer_url: null, screenshots: null,
    tags: ['rpg', 'narrative', 'post-apocalyptic', 'choices'], genre: 'RPG',
    min_requirements: null, rec_requirements: null,
    file_size_mb: 12000, version: '0.5.0', is_featured: false,
    rating_avg: 4.8, rating_count: 45, download_count: 670,
  },
];

const FALLBACK_MC_SERVERS: McServer[] = [
  {
    id: 'mc-1', name: 'Hypixel', description: 'Le plus grand serveur Minecraft au monde avec des mini-jeux variés.',
    address: 'mc.hypixel.net', port: 25565, version: '1.8 - 1.20.4', max_players: 100000,
    icon_url: null, banner_url: null,
    tags: ['minigames', 'pvp', 'skyblock', 'bedwars'], website_url: 'https://hypixel.net',
    discord_url: null, is_featured: true, player_count: 45000, uptime_percent: 99.9,
  },
  {
    id: 'mc-2', name: 'Erozion SMP', description: 'Le serveur survival officiel de la communauté Erozion.',
    address: 'smp.erozion.net', port: 25565, version: '1.20.4', max_players: 200,
    icon_url: null, banner_url: null,
    tags: ['survival', 'community', 'smp', 'vanilla'], website_url: null,
    discord_url: null, is_featured: true, player_count: 42, uptime_percent: 98.5,
  },
  {
    id: 'mc-3', name: 'Erozion Creative', description: 'Serveur créatif Erozion - Construisez sans limites.',
    address: 'creative.erozion.net', port: 25565, version: '1.20.4', max_players: 100,
    icon_url: null, banner_url: null,
    tags: ['creative', 'building', 'plots'], website_url: null,
    discord_url: null, is_featured: false, player_count: 18, uptime_percent: 97.0,
  },
];
